import axios from "axios";
import Vue from "vue";
import constant from "../constant";
import store from "../store";
import router from "../router";

const startLoading = () => {
  if (
    Vue.prototype.$loading &&
    typeof Vue.prototype.$loading.start === "function"
  ) {
    Vue.prototype.$loading.start();
  }
};

const stopLoading = () => {
  if (
    Vue.prototype.$loading &&
    typeof Vue.prototype.$loading.stop === "function"
  ) {
    Vue.prototype.$loading.stop();
  }
};

const showToast = (type, message) => {
  if (
    Vue.prototype.$toast &&
    typeof Vue.prototype.$toast[type] === "function"
  ) {
    Vue.prototype.$toast[type](message);
  }
};

const openConfirm = (options) => {
  if (typeof Vue.prototype.$confirm === "function") {
    return Vue.prototype.$confirm(options);
  }
  return Promise.resolve(true);
};

let isHandlingUnauthenticated = false;

const redirectToLogin = () => {
  const alreadyOnLogin =
    router?.currentRoute?.name === "login" ||
    router?.currentRoute?.path === "/login";

  if (alreadyOnLogin) {
    return;
  }

  if (router) {
    router.push({ name: "login" }).catch((navigationError) => {
      if (navigationError?.name !== "NavigationDuplicated") {
        console.error(
          "Navigation vers la page de connexion échouée:",
          navigationError
        );
        window.location.href = "/login";
      }
    });
  } else {
    window.location.href = "/login";
  }
};

const handleUnauthenticated = () => {
  if (isHandlingUnauthenticated) {
    return;
  }
  isHandlingUnauthenticated = true;

  localStorage.removeItem(constant.authToken);
  localStorage.removeItem(constant.authUser);
  localStorage.removeItem(constant.userRoleKey);

  showToast(
    "error",
    "Votre session a expiré. Veuillez vous reconnecter pour continuer."
  );

  const logoutPromise =
    typeof store?.dispatch === "function"
      ? store.dispatch("auth/logout").catch((error) => {
          console.error("Erreur lors de la déconnexion forcée:", error);
          redirectToLogin();
        })
      : Promise.resolve().then(() => redirectToLogin());

  Promise.resolve(logoutPromise)
    .then(() => {
      if (!router || router.currentRoute?.name !== "login") {
        redirectToLogin();
      }
    })
    .finally(() => {
      setTimeout(() => {
        isHandlingUnauthenticated = false;
      }, 500);
    });
};

const isLoginRequest = (config) => {
  const requestUrl = String(config?.url || "");
  return requestUrl.includes("auth/login");
};

export default {
  install(VueInstance) {
    if (VueInstance.prototype.$api) {
      return;
    }

    const instance = axios.create({
      baseURL: process.env.VUE_APP_API_URL || constant.url,
      timeout: 30000, // Increased timeout for mobile networks
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        // Removed X-Requested-With - it causes CORS preflight issues in Android WebView
      },
    });

    instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(constant.authToken);
        if (token) {
          config.headers["Authorization"] = `Bearer ${token.replaceAll(
            '"',
            ""
          )}`;
        }

        // Add headers for better compatibility with mobile and CORS
        config.headers["Accept"] = "application/json";
        // Only set Content-Type to application/json if it's not FormData
        // FormData needs to set its own Content-Type with boundary
        if (!(config.data instanceof FormData)) {
          config.headers["Content-Type"] = "application/json";
        }

        // Log request for debugging on mobile
        console.log("API Request:", {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
          headers: config.headers,
        });

        return config;
      },
      (error) => {
        console.error("API Request Error:", error);
        return Promise.reject(error);
      }
    );

    instance.interceptors.response.use(
      (response) => {
        console.log("API Response:", {
          status: response.status,
          statusText: response.statusText,
          url: response.config?.url,
          headers: response.headers,
          data: response.data,
          dataType: typeof response.data,
          dataLength: response.data
            ? typeof response.data === "string"
              ? response.data.length
              : Object.keys(response.data).length
            : 0,
        });

        // Check for empty response (common IIS issue)
        if (
          !response.data ||
          (typeof response.data === "object" &&
            Object.keys(response.data).length === 0)
        ) {
          console.warn(
            "Empty response detected in interceptor - possible IIS/CORS issue"
          );
          console.warn("Response status:", response.status);
          console.warn("Response headers:", response.headers);
        }

        return response;
      },
      (error) => {
        console.error("API Response Error:", {
          message: error.message,
          code: error.code,
          response: error.response,
          request: error.request,
          config: error.config,
        });

        // Handle CORS and network errors specifically
        if (!error.response) {
          // Network error or CORS issue
          console.error("Network error - possible CORS or connectivity issue");
          if (
            error.message === "Network Error" ||
            error.code === "NETWORK_ERROR"
          ) {
            showToast(
              "error",
              "Erreur de connexion réseau. Vérifiez votre connexion internet et que le serveur est accessible."
            );
          }
        }

        const responseStatus = error.response?.status;
        const responseMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          (typeof error.response?.data === "string" ? error.response.data : "");

        if (
          !isLoginRequest(error.config) &&
          (responseStatus === 401 ||
            responseStatus === 419 ||
            (typeof responseMessage === "string" &&
              responseMessage.trim().toLowerCase() === "unauthenticated."))
        ) {
          handleUnauthenticated();
        }

        return Promise.reject(error);
      }
    );

    window.addEventListener("storage", (event) => {
      if (
        event.key === constant.userRoleKey &&
        store &&
        store._mutations &&
        store._mutations["SET_USER_ROLE"]
      ) {
        store.commit("SET_USER_ROLE", event.newValue);
      }
    });

    VueInstance.prototype.$api = {
      instance,
      async show(url) {
        startLoading();
        try {
          const response = await instance.get(url);
          return response;
        } catch (error) {
          console.error("API Show Error:", error);
          const errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Une erreur est survenue lors de la récupération des données.";
          showToast("error", errorMessage);
          throw error;
        } finally {
          stopLoading();
        }
      },
      async fetch(url, params = {}) {
        startLoading();
        try {
          const response = await instance.get(url, { params });
          return response.data;
        } catch (error) {
          console.error("API Fetch Error:", error);
          const errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Une erreur est survenue lors du chargement des données.";
          showToast("error", errorMessage);
          throw error;
        } finally {
          stopLoading();
        }
      },
      async create(url, data, options = {}) {
        // Only show confirmation if explicitly set to true
        if (options.confirm === true) {
          const confirmed = await openConfirm({
            title: "Confirmation",
            message: "Voulez-vous vraiment créer cet élément ?",
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
          });
          if (!confirmed) return;
        }

        startLoading();
        try {
          const response = await instance.post(url, data);
          if (response.data && response.data.status) {
            showToast("success", response.data.message);
          }
          return response;
        } catch (error) {
          console.error("API Create Error:", error);
          const errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Une erreur est survenue lors de la création.";
          showToast("error", errorMessage);
          throw error;
        } finally {
          stopLoading();
        }
      },
      async update(url, data, options = {}) {
        // Only show confirmation if explicitly set to true
        if (options.confirm === true) {
          const confirmed = await openConfirm({
            title: "Confirmation",
            message: "Voulez-vous vraiment modifier cet élément ?",
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
          });
          if (!confirmed) return;
        }

        startLoading();
        try {
          const response = await instance.put(url, data);
          if (response.data && response.data.status) {
            showToast("success", response.data.message);
          }
          return response;
        } catch (error) {
          console.error("API Update Error:", error);
          const errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Une erreur est survenue lors de la mise à jour.";
          showToast("error", errorMessage);
          throw error;
        } finally {
          stopLoading();
        }
      },
      async delete(url, options = {}) {
        // Only show confirmation if explicitly set to true
        if (options.confirm === true) {
          const confirmed = await openConfirm({
            title: "Confirmation",
            message: "Voulez-vous vraiment supprimer cet élément ?",
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
          });
          if (!confirmed) return;
        }

        startLoading();
        try {
          const response = await instance.delete(url);
          if (response.data && response.data.status) {
            showToast("success", response.data.message);
          }
          return response;
        } catch (error) {
          console.error("API Delete Error:", error);
          const errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Une erreur est survenue lors de la suppression.";
          showToast("error", errorMessage);
          throw error;
        } finally {
          stopLoading();
        }
      },
    };
  },
};
